import { useEffect, useState } from "react";
import { graphql, readInlineData } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { DefaultContentTypes } from "scmp-app/components/search/consts";
import type { hooksTypeMenuArticleSearchConnection$key } from "scmp-app/queries/__generated__/hooksTypeMenuArticleSearchConnection.graphql";

import { type TypeOption, TypeOptions } from "./const";

export type TypeOptionWithCount = TypeOption & {
  count?: string;
};

export function useTypeMenuOptions(reference: hooksTypeMenuArticleSearchConnection$key) {
  const data = readInlineData(
    graphql`
      fragment hooksTypeMenuArticleSearchConnection on ArticleSearchConnection @inline {
        aggregations {
          articleTypeId {
            buckets {
              doc_count
              key
            }
          }
          contentType {
            buckets {
              doc_count
              key
            }
          }
        }
      }
    `,
    reference,
  );

  const [options, setOptions] = useState<TypeOptionWithCount[]>([]);

  useEffect(() => {
    if (
      !notEmpty(data.aggregations?.articleTypeId.buckets) ||
      !notEmpty(data.aggregations?.contentType.buckets)
    )
      return;

    const articleTypeBuckets = data.aggregations.articleTypeId.buckets;
    const contentTypeBuckets = data.aggregations.contentType.buckets;

    const bucks = new Map([
      ...articleTypeBuckets.map(({ doc_count, key }) => [key, doc_count] as const),
      ...contentTypeBuckets.map(({ doc_count, key }) => [key.toLowerCase(), doc_count] as const),
    ]);

    setOptions([
      { isContentType: true, text: "All", value: [...DefaultContentTypes] },
      ...TypeOptions.map(option => {
        if (option.isContentType) {
          return {
            ...option,
            count: `${option.value.reduce(
              (sum, value) => sum + (bucks.get(value.toLowerCase()) ?? 0),
              0,
            )}`,
          };
        }
        return {
          ...option,
          count: `${option.value.reduce((sum, value) => sum + (bucks.get(value) ?? 0), 0)}`,
        };
      }),
    ]);
  }, [data.aggregations?.articleTypeId.buckets, data.aggregations?.contentType.buckets]);

  return { options };
}
